import type { WechatIdentityLinkChannel } from '@/types/api'

export type WechatIdentityReturnPath = '/u/settings' | '/lawyer/settings'

export interface WechatIdentityLinkIntent {
  version: 1
  purpose: 'identity-link'
  returnTo: WechatIdentityReturnPath
  linkSessionId: string
  channel: WechatIdentityLinkChannel
  expireAt?: string
  createdAt: number
}

const IDENTITY_LINK_INTENT_KEY = 'legal-web:wechat-identity-link-intent'
const IDENTITY_LINK_INTENT_TTL = 10 * 60_000

export function getWechatCallbackUrl(): string {
  return new URL('/auth/wechat-callback', window.location.origin).toString()
}

export function rememberWechatIdentityLinkIntent({
  channel,
  expireAt,
  linkSessionId,
  returnTo,
}: Pick<WechatIdentityLinkIntent, 'channel' | 'expireAt' | 'linkSessionId' | 'returnTo'>): boolean {
  const intent: WechatIdentityLinkIntent = {
    version: 1,
    purpose: 'identity-link',
    returnTo,
    linkSessionId,
    channel,
    ...(expireAt ? { expireAt } : {}),
    createdAt: Date.now(),
  }
  try {
    window.sessionStorage.setItem(IDENTITY_LINK_INTENT_KEY, JSON.stringify(intent))
    return true
  } catch {
    // 无法可靠区分登录和绑定回调时，调用方必须停止跳转
    return false
  }
}

export function readWechatIdentityLinkIntent(): WechatIdentityLinkIntent | null {
  try {
    const raw = window.sessionStorage.getItem(IDENTITY_LINK_INTENT_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<WechatIdentityLinkIntent>
    const returnTo = normalizeWechatIdentityReturnPath(value.returnTo)
    if (
      value.version !== 1 ||
      value.purpose !== 'identity-link' ||
      typeof value.linkSessionId !== 'string' ||
      !value.linkSessionId ||
      !isWechatIdentityLinkChannel(value.channel) ||
      typeof value.createdAt !== 'number' ||
      Date.now() - value.createdAt > IDENTITY_LINK_INTENT_TTL ||
      isExpired(value.expireAt)
    ) {
      clearWechatIdentityLinkIntent()
      return null
    }
    return {
      version: 1,
      purpose: 'identity-link',
      returnTo,
      linkSessionId: value.linkSessionId,
      channel: value.channel,
      ...(value.expireAt ? { expireAt: value.expireAt } : {}),
      createdAt: value.createdAt,
    }
  } catch {
    clearWechatIdentityLinkIntent()
    return null
  }
}

export function clearWechatIdentityLinkIntent(): void {
  try {
    window.sessionStorage.removeItem(IDENTITY_LINK_INTENT_KEY)
  } catch {
    // 清理失败不影响登录或绑定主流程
  }
}

export function normalizeWechatIdentityReturnPath(raw: unknown): WechatIdentityReturnPath {
  return raw === '/lawyer/settings' ? '/lawyer/settings' : '/u/settings'
}

function isWechatIdentityLinkChannel(value: unknown): value is WechatIdentityLinkChannel {
  return (
    value === 'web_open_qr' ||
    value === 'mp_oauth' ||
    value === 'mp_cross_browser' ||
    value === 'miniapp_bridge'
  )
}

function isExpired(expireAt: unknown): boolean {
  if (typeof expireAt !== 'string' || !expireAt) return false
  const timestamp = new Date(expireAt).getTime()
  return Number.isFinite(timestamp) && timestamp <= Date.now()
}
